import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { BroadcastService } from '../../service/broadcast.service';
import { ActiveBroadcast } from '../../types/broadcast';
import { BroadcastItemComponent } from '../broadcast-item/broadcast-item.component';

const DISMISSED_BROADCASTS_KEY = 'DISMISSED_BROADCASTS';

@Component({
    selector: 'juyo-active-broadcasts',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './active-broadcasts.scss',
    imports: [BroadcastItemComponent],
    template: `
        @for (broadcast of broadcasts(); track broadcast.id) {
            <juyo-broadcast-item
                [broadcast]="broadcast"
                (dontShowAgain)="onDontShowAgain(broadcast)"
                (closed)="onClose(broadcast)"
            />
        }
    `,
})
export class ActiveBroadcastsComponent {
    private broadcastService = inject(BroadcastService);
    private destroyRef = inject(DestroyRef);

    protected broadcasts = signal<ActiveBroadcast[]>([]);

    constructor() {
        this.loadActiveBroadcasts();
    }

    private loadActiveBroadcasts(): void {
        this.broadcastService
            .getActiveBroadcasts()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((broadcasts) => {
                const dismissed = this.readDismissed();
                this.broadcasts.set(broadcasts.filter((broadcast) => !dismissed.includes(broadcast.id)));
            });
    }

    protected onDontShowAgain(broadcast: ActiveBroadcast): void {
        const dismissed = this.readDismissed();
        if (!dismissed.includes(broadcast.id)) {
            this.writeDismissed([...dismissed, broadcast.id]);
        }
        this.onClose(broadcast);
    }

    protected onClose(broadcast: ActiveBroadcast): void {
        this.broadcasts.update((list) => list.filter((item) => item.id !== broadcast.id));
    }

    /** The dismissed ids are kept as a single list so we do not add a storage entry per broadcast. */
    private readDismissed(): string[] {
        const stored = localStorage.getItem(DISMISSED_BROADCASTS_KEY);
        if (!stored) {
            return [];
        }

        try {
            const parsed: unknown = JSON.parse(stored);
            return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
        } catch {
            return [];
        }
    }

    private writeDismissed(ids: string[]): void {
        localStorage.setItem(DISMISSED_BROADCASTS_KEY, JSON.stringify(ids));
    }
}
